import { AxiosResponse } from 'axios';
import { createApiClient, extractApiError } from 'services/apiClient';
import TenantService from 'services/TenantService';
import type { PortalComponentId, PortalComponentsResponse } from 'types/PortalComponents';

const apiClient = createApiClient('PORTAL_COMPONENTS', { logging: false });

/**
 * The portal's one source of tab visibility, and the admin Portal tab's data
 * (work_with_claude/portal/portal-component-gating.plan.md §3.4).
 *
 * Reads go to `GET /api/portal/{tenantId}/components`. Writes are ordinary
 * partial tenant-config updates: the tenant's own switch lives in
 * `portal.components`, the license in `license.portal.components` (the backend
 * refuses a license write from a non-admin with 403).
 */
export default class PortalComponentsService {
  static async get(tenantId: string, signal?: AbortSignal): Promise<PortalComponentsResponse> {
    try {
      const response: AxiosResponse<PortalComponentsResponse> = await apiClient.get(
        `/portal/${encodeURIComponent(tenantId)}/components`,
        { signal },
      );
      return response.data;
    } catch (error: any) {
      throw extractApiError(error, 'Failed to load portal components');
    }
  }

  /** The tenant's switch: `portal.components.<id>`. Owner or admin. */
  static async setEnabled(tenantId: string, id: PortalComponentId, enabled: boolean): Promise<void> {
    await TenantService.updateTenantConfigurationPartial(tenantId, {
      portal: { components: { [id]: enabled } },
    });
  }

  /** The license: `license.portal.components.<id>`. Admin only. */
  static async setLicensed(tenantId: string, id: PortalComponentId, licensed: boolean): Promise<void> {
    await TenantService.updateTenantConfigurationPartial(tenantId, {
      license: { portal: { components: { [id]: licensed } } },
    });
  }
}
